<template>
  <div>
    <div
      v-show="item.externally_managed !== true && workflow === $options.create"
      :class="$style.inputRow"
    >
      <div :class="[$style.flexSingleWidth, customStyles]">
        <label for="specify-password">
          <input
            id="specify-password"
            v-model="initialPasswordToggle"
            :class="[$style.inputWithSubLabel, $style.inputPosition]"
            name="Specify Password"
            type="checkbox"
            @change="removeHiddenValues($event.target.checked)"
          >
          <span>Specify initial password</span>
          <small
            :class="$style.subLabel"
          >
            Use this setting if the user's email address does not exist yet.
          </small>
        </label>
      </div>
    </div>
    <transition-group
      :enterActiveClass="$style.fadeEnterActive"
      :enterClass="$style.fadeEnter"
      :leaveActiveClass="$style.fadeLeaveActive"
      :leaveToClass="$style.fadeLeaveTo"
      name="fade"
      tag="div"
    >
      <template v-if="showResetPasswordButton">
        <div
          key="inputs"
          :class="[$style.inputRow, $style.rowSpacing]"
        >
          <ButtonSecondary
            :data-test-id="$testId('changePasswordButton')"
            :text="resetPasswordButtonLabel"
            @click="handlePasswordChangeClick"
          />
          <PasswordUpdateModal
            :fullName="displayName"
            :openModal="showPasswordChangeModal"
            :passwordPolicy="passwordPolicy"
            :user="systemUser"
            @cancel="handlePasswordChangeModalCancel"
            @close="handlePasswordChangeModalClose"
          />
        </div>
        <CheckboxWithLabel
          key="passwordExpire"
          :class="[$style.inputRow]"
          data-test-id="SpecifyPassword__passwordExpiration"
          :disabled="!passwordExpirationEnabled"
          label="Set password to expire"
          name="Password Expire"
          :preventDefault="true"
          :value="hasPasswordExpirationDate"
          @change="handlePasswordExpiresChange"
        >
          <div
            v-if="passwordExpirationEnabled"
            :class="$style.subLabelCheckbox"
            data-test-id="SpecifyPassword__passwordExpirationSubLabel"
          >
            {{ expirationDate }}
          </div>
          <small :class="$style.subLabelCheckbox">
            <Link
              text="Update Password Aging Settings"
              :to="$options.passwordAgingLink"
            />
          </small>
        </CheckboxWithLabel>
      </template>
      <template v-else>
        <div
          v-show="initialPasswordToggle"
          key="inputs"
          :class="$style.inputRow"
        >
          <div
            :class="$style.passwordComplexityWrapper"
          >
            <PasswordComplexity
              v-model="createPassword"
              :passwordPolicy="passwordPolicy"
              :useBackendValidations="false"
              :username="systemUser.username"
              @input="handlePasswordChange"
              @isValidPassword="handleIsValidPassword"
            />
            <hr>
            <UserPasswordOptions
              :value="item.password_expired"
              @input="handleRequirePasswordChange"
            />
          </div>
        </div>
      </template>
      <template v-if="!showResetPasswordButton">
        <div
          v-if="workflow === $options.update"
          key="radios"
          :class="[$style.inputRow, $style.revealedRowPosition]"
        >
          <div :class="[$style.flexSingleWidth, $style.expiresOnDate, customStyles]">
            <input
              id="password-expires"
              v-validate="{
                required: workflow == $options.update
              }"
              :checked="hasPasswordExpirationDate"
              :class="$style.expirationRadio"
              :disabled="!passwordExpirationEnabled"
              name="Password Expiration Method"
              type="radio"
              :value="$options.passwordExpirationDate"
              @change="passwordMethodChange"
            >
            <label
              :class="$style['radio-label']"
              for="password-expires"
            >
              <PasswordExpirationLabelText
                :activated="item.activated === true"
                :enablePasswordExpirationInDays="passwordExpirationEnabled"
                :passwordExpirationDate="item.password_expiration_date"
                :passwordExpirationDateSelected="hasPasswordExpirationDate"
                :passwordExpirationInDays="passwordPolicy.passwordExpirationInDays"
                :passwordIsExpired="item.password_expired === true"
                :userPassword="item.password"
              />
            </label>
          </div>
          <div :class="[$style.flexSingleWidth, customStyles]">
            <input
              id="password-never-expires"
              v-validate="{
                required: workflow == $options.update
              }"
              :checked="passwordExpirationMethod === $options.passwordNeverExpires"
              :class="$style.expirationRadio"
              :disabled="!passwordExpirationEnabled"
              name="Password Expiration Method"
              type="radio"
              :value="$options.passwordNeverExpires"
              @change="passwordMethodChange"
            >
            <label
              :class="$style['radio-label']"
              for="password-never-expires"
            >
              <div>
                Password never expires
              </div>
            </label>
          </div>
        </div>
      </template>
    </transition-group>
  </div>
</template>

<script>
import {
  ButtonSecondary,
  Link,
} from '@jumpcloud/ui-components';
import PasswordUpdateModal from 'client/systemusers/Modals/PasswordUpdateModal.vue';

import { ItemActionConsts, ModuleConsts } from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import { passwordAgingConst } from 'client/settings/Security/SecuritySettingsConstants';
import { systemUserMessageText } from 'client/systemusers/constants/SystemUserConstants';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import CheckboxWithLabel from 'client/components/FormValidation/CheckboxWithLabel';
import DateUtil from 'client/util/DateUtil';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import PasswordComplexity from 'client/systemusers/components/PasswordComplexity.vue';
import PasswordExpirationLabelText from 'client/systemusers/UserDetails/SpecifyPassword/PasswordExpirationLabelText.vue';
import UserItemStates from 'components/mixins/UserItemStates';
import UserPasswordOptions from 'client/systemusers/components/UserPasswordOptions.vue';

const passwordNeverExpires = 'password_never_expires';
const passwordExpirationDate = 'password_expiration_date';

const passwordFieldName = 'password';

const systemUserModel = 'SystemUserModel';
const { passwordStates } = ModuleConsts.systemUser;

export default {
  name: 'SpecifyPassword',

  inject: ['$validator'],

  components: {
    ButtonSecondary,
    CheckboxWithLabel,
    Link,
    PasswordComplexity,
    PasswordExpirationLabelText,
    PasswordUpdateModal,
    UserPasswordOptions,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    UserItemStates,
  ],

  props: {
    customStyles: {
      type: [String, Array],
      default: '',
    },

    valueChange: {
      type: Function,
      required: true,
    },

    workflow: {
      type: String,
      default: ItemActionConsts.create,
      validator(value) {
        return ItemActionConsts[value] !== undefined;
      },
    },
  },

  data() {
    return {
      confirmPassword: '',
      createPassword: '',
      initialPasswordToggle: false,
      disableDeviceLockoutTooltip: {
        text: 'This does not exclude the user from user portal or change password failed logon attempts.  It only excludes the device logon attempts.',
      },
      passwordFieldNames: {
        create: 'Create Password',
        confirm: 'Confirm Password',
      },
      showPasswordChangeModal: false,
    };
  },

  create: ItemActionConsts.create,

  passwordAgingLink: `/settings/security#${passwordAgingConst}`,

  passwordExpirationDate,

  passwordNeverExpires,

  update: ItemActionConsts.update,

  computed: {
    ...mapState('SettingsModule', ['passwordPolicy']),

    ...mapGetters(systemUserModel, {
      systemUser: 'item',
    }),

    ...mapGetters('SystemUserCollection', {
      getDisplayName: 'getDisplayName',
    }),

    displayName() {
      return this.getDisplayName(this.systemUser);
    },

    expirationDate() {
      const userPasswordNeverExpires = this.item.password_never_expires;

      let expirationDateSubText = '';

      if (!userPasswordNeverExpires) {
        const date = DateUtil.formatDate(
          this.item.password_expiration_date || this.orgExpirationDate,
        );
        expirationDateSubText = `Expiration date for this user: ${date}`;
      }

      return expirationDateSubText;
    },

    hasPasswordExpirationDate() {
      return this.passwordExpirationMethod === passwordExpirationDate;
    },

    orgExpirationDate() {
      const { passwordExpirationInDays } = this.passwordPolicy;

      return DateUtil.add(undefined, passwordExpirationInDays, 'days');
    },

    passwordExpirationEnabled() {
      return this.passwordPolicy.enablePasswordExpirationInDays;
    },

    passwordExpirationMethod() {
      const userPasswordNeverExpires = this.item.password_never_expires;
      const orgEnablePasswordExpire = this.passwordPolicy.enablePasswordExpirationInDays;

      let method = passwordExpirationDate;

      if (userPasswordNeverExpires || !orgEnablePasswordExpire) {
        method = passwordNeverExpires;
      }

      return method;
    },

    resetPasswordButtonLabel() {
      return this.passwordStatus === passwordStates.pending ? 'Set Password' : 'Reset Password';
    },

    showResetPasswordButton() {
      return this.workflow === this.$options.update;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  methods: {
    ...mapActions(systemUserModel, [
      'activatePassword',
    ]),

    cancelForcePasswordChange() {
      // Force checkbox to re-render
      // @see https://michaelnthiessen.com/force-re-render/
      ModalEventBus.close();
    },

    continueForcePasswordChange() {
      // Expire the user immediately after creation
      this.valueChange(true, 'password_expired');
      ModalEventBus.close();
    },

    handleIsValidPassword(isValidPassword) {
      if (!isValidPassword && this.initialPasswordToggle) {
        this.$validator.errors.add({
          field: passwordFieldName,
          msg: 'The password is not valid',
        });
      } else {
        this.$validator.errors.remove(passwordFieldName);
      }
    },

    handlePasswordChange(value) {
      this.valueChange(value, 'password');
    },

    handlePasswordChangeClick() {
      this.showPasswordChangeModal = true;
    },

    handlePasswordChangeModalCancel() {
      this.hidePasswordModal();
    },

    handlePasswordChangeModalClose(saved) {
      this.hidePasswordModal();

      if (saved) {
        const userPasswordNeverExpires = this.item.password_never_expires;
        let expirationDate = '';

        if (!userPasswordNeverExpires) {
          expirationDate = DateUtil.formatDate(this.orgExpirationDate);
        }
        this.activatePassword({ expirationDate });
      }
    },

    handlePasswordExpiresChange(checked) {
      this.valueChange(!checked, 'password_never_expires');
    },

    handleRequirePasswordChange(checked) {
      const componentProps = {
        loginChange: true,
        handleCancel: this.cancelForcePasswordChange,
        handleContinue: this.continueForcePasswordChange,
        infoText: systemUserMessageText.forcePasswordInfoText,
      };

      if (checked && !this.passwordPolicy.enableDaysAfterExpirationToSelfRecover) {
        ModalEventBus.open({
          component: 'ForcePasswordChangeModal',
          componentProps,
          preventCloseOnOverlayClick: true,
        });
      } else {
        this.valueChange(checked, 'password_expired');
      }
    },

    hidePasswordModal() {
      this.showPasswordChangeModal = false;
    },

    passwordMethodChange($event) {
      this.valueChange(
        ($event.target.value === passwordNeverExpires),
        'password_never_expires',
      );
    },

    removeHiddenValues(checked) {
      if (checked === true) return;

      this.$validator.errors.remove(passwordFieldName);

      this.unset({ key: 'password' });
      this.createPassword = '';
      this.confirmPassword = '';
    },
  },
};
</script>

<style module>
@import '../../../css/flex.css';
@import '../../../components/FormValidation/css/formFields.css';

@value inputContainerMargin from '~components/FormValidation/css/FormValidationFields.css';
@value (jcOrange) from '~css/colors.css';

.inputWithSubLabel {
  vertical-align: top;
}

.subLabel {
  display: block;
  margin-left: 25px;
}

.subLabelCheckbox {
  color: var(--jc-color-neutral-text-descriptive);
  display: block;
  margin-left: 21px;
}

.errorSpacing {
  margin-bottom: 5px;
}

.revealedRowPosition {
  margin-bottom: 1rem;
}

input[type="radio"].expirationRadio {
  float: left;
  margin: 5px 3px 0 0;
}

.radio-label {
  composes: label-font from "~components/FormValidation/css/formFields.css";
  composes: label-typography from "~components/FormValidation/css/formFields.css";
  line-height: 1rem;
  margin: 3px 12px;
}

.expiresOnDate .passwordExpirationDate {
  color: jcOrange;
  font-size: larger;
  font-weight: 555;
}

input:disabled + label.radio-label {
  cursor: not-allowed;
}

.passwordComplexityWrapper {
  border: 1px solid var(--jcGrey200);
  border-radius: var(--jc-border-radius);
  margin: 1.25rem 0 var(--jc-spacer-medium) 0;
  padding: var(--jc-spacer) var(--jc-spacer-medium);
  padding-bottom: 0;
}

.passwordComplexityWrapper hr {
  border: 0;
  border-bottom: var(--jc-border-width) solid var(--jcGrey200);
  margin: 1.25rem 0 var(--jc-spacer-medium) 0;
}

</style>
